<template>
  <transition name="cookie-fade">
    <section
      v-if="visible"
      class="cookie-banner"
      role="region"
      aria-labelledby="cookie-banner-title"
    >
      <div class="cookie-banner__inner">
        <div class="cookie-banner__copy">
          <h2 id="cookie-banner-title" class="cookie-banner__title">We value your privacy</h2>
          <p class="cookie-banner__text">
            We use essential cookies to run IMPROVE. With your permission we also use
            Google Analytics to understand how our sign up and sign in pages are used,
            and show videos from YouTube and Synthesia on our website. Your choice
            applies across improve.online. Read our
            <a :href="privacyPolicyUrl" class="cookie-banner__link">Privacy Policy</a>.
          </p>
          <div v-if="managing" class="cookie-banner__choices">
            <label class="cookie-choice">
              <input v-model="choices.analytics" type="checkbox" class="cookie-choice__box" />
              <span><strong>Analytics</strong>: Google Analytics, to see which pages help visitors</span>
            </label>
            <label class="cookie-choice">
              <input v-model="choices.marketing" type="checkbox" class="cookie-choice__box" />
              <span><strong>Videos</strong>: the YouTube and Synthesia players on our website</span>
            </label>
          </div>
        </div>
        <div class="cookie-banner__actions">
          <button type="button" class="cookie-btn cookie-btn--reject" @click="reject">
            Reject all
          </button>
          <button type="button" class="cookie-btn cookie-btn--accept" @click="acceptAll">
            Accept all
          </button>
          <button
            type="button"
            class="cookie-banner__manage"
            @click="managing ? saveChoices() : (managing = true)"
          >
            {{ managing ? "Save my choices" : "Manage choices" }}
          </button>
        </div>
      </div>
    </section>
  </transition>
</template>

<script>
import { EventBus } from "@/event-bus";
import { websiteUrl } from "@/config.js";
import { setConsent, hasResponded, getConsent } from "@/utils/consent";
import { onConsentChange } from "@/utils/analytics";

/**
 * CookieBanner
 *
 * UK PECR / UK GDPR consent for the app's public pages (sign up, sign in, the
 * password pages, thank you), shown to signed out visitors who have not chosen
 * yet, here or on the website: the choice is shared (utils/consent.js).
 * Accept all and Reject all are equally easy; "Manage choices" allows each
 * category on its own. The "Cookie settings" link (CookieSettingsLink) reopens
 * it, so withdrawing is as easy as consenting. Signed in pages measure nothing
 * (decision D4 is open), so it never shows there.
 *
 * A bar at the top of the page, in the flow, unlike the website's floating
 * card: these pages are a form and little else, and a floating card covered
 * the LOGIN button on the login page (both sides of the page hold a form on
 * one page or another, and a phone has no free corner). In the flow it pushes
 * the page down instead, so it never covers anything. Same copy and choices as
 * improve-online's CookieBanner.vue.
 *
 * Plan: docs/plans/analytics-newco-replacement-2026-10.md in the tracker repo.
 */
export default {
  name: "CookieBanner",
  data() {
    return {
      opened: false,
      managing: false,
      responded: hasResponded(),
      choices: { analytics: false, marketing: false },
      privacyPolicyUrl: websiteUrl("/privacy-policy"),
    };
  },
  computed: {
    eligible() {
      const matched = this.$route.matched;
      const isPublic = matched.length > 0 && matched.every((r) => r.meta && r.meta.public);
      return isPublic && !this.$store.getters.isLoggedIn;
    },
    visible() {
      return this.eligible && (this.opened || !this.responded);
    },
  },
  created() {
    EventBus.$on("open-cookie-banner", this.open);
  },
  beforeDestroy() {
    EventBus.$off("open-cookie-banner", this.open);
  },
  methods: {
    open() {
      // Start the toggles from the stored choice, so reopening shows it, and
      // bring the bar into view: it sits at the top of the page.
      const stored = getConsent() || {};
      this.choices = { analytics: !!stored.analytics, marketing: !!stored.marketing };
      this.managing = false;
      this.opened = true;
      window.scrollTo(0, 0);
    },
    acceptAll() {
      this.apply({ analytics: true, marketing: true });
    },
    reject() {
      this.apply({ analytics: false, marketing: false });
    },
    saveChoices() {
      this.apply(this.choices);
    },
    apply(choice) {
      // Remember the choice, close, then load what was granted and stop what
      // was withdrawn (onConsentChange deletes Google's cookies and reloads
      // when analytics was running on this page).
      setConsent(choice);
      this.responded = true;
      this.opened = false;
      onConsentChange(this.$route, this.$store.getters.isLoggedIn);
    },
  },
};
</script>

<style scoped>
.cookie-banner {
  position: relative;
  z-index: 5;
  width: 100%;
  background: #ffffff;
  border-bottom: 1px solid #e3e8ef;
  box-shadow: 0 2px 12px rgba(0, 36, 88, 0.08);
  box-sizing: border-box;
  text-align: left;
}

.cookie-banner__inner {
  display: flex;
  align-items: center;
  gap: 24px;
  max-width: 1180px;
  margin: 0 auto;
  padding: 16px 24px;
  box-sizing: border-box;
}

.cookie-banner__copy {
  flex: 1 1 auto;
  min-width: 0;
}

.cookie-banner__title {
  font-family: "Sora", sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #002458;
  margin: 0 0 4px;
  line-height: 1.3;
}

.cookie-banner__text {
  font-size: 14px;
  color: #4a4a4a;
  line-height: 1.5;
  margin: 0;
}

.cookie-banner__link {
  color: #08a0a8 !important;
  font-weight: 600;
  text-decoration: none;
}

.cookie-banner__link:hover {
  text-decoration: underline;
}

.cookie-banner__actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 0 0 auto;
}

/* Both buttons share identical size and weight so Reject is exactly as easy
   to use as Accept (UK PECR / ICO guidance). */
.cookie-btn {
  min-width: 128px;
  font-family: "Sora", sans-serif;
  font-size: 14px;
  font-weight: 600;
  padding: 10px 16px;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.cookie-btn--accept {
  background: #08a0a8;
  color: #ffffff;
  border: 1.5px solid #08a0a8;
}

.cookie-btn--accept:hover {
  background: #078289;
  border-color: #078289;
}

.cookie-btn--reject {
  background: #ffffff;
  color: #002458;
  border: 1.5px solid #002458;
}

.cookie-btn--reject:hover {
  background: #f3f4f6;
}

.cookie-btn:focus-visible,
.cookie-banner__manage:focus-visible {
  outline: 2px solid #002458;
  outline-offset: 2px;
}

.cookie-banner__choices {
  margin: 12px 0 0;
}

.cookie-choice {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 14px;
  color: #4a4a4a;
  line-height: 1.45;
  margin-bottom: 8px;
  cursor: pointer;
}

.cookie-choice__box {
  width: 18px;
  height: 18px;
  margin-top: 1px;
  flex: 0 0 auto;
  accent-color: #08a0a8;
}

.cookie-banner__manage {
  background: none;
  border: none;
  padding: 4px 6px;
  font-family: "Sora", sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #002458;
  text-decoration: underline;
  cursor: pointer;
  white-space: nowrap;
}

.cookie-fade-enter-active,
.cookie-fade-leave-active {
  transition: opacity 0.2s ease;
}

.cookie-fade-enter,
.cookie-fade-leave-to {
  opacity: 0;
}

/* Tablets and phones: the buttons go under the text, full width. */
@media (max-width: 860px) {
  .cookie-banner__inner {
    flex-direction: column;
    align-items: stretch;
    gap: 14px;
    padding: 14px 16px;
  }

  .cookie-banner__actions {
    flex-wrap: wrap;
  }

  .cookie-btn {
    flex: 1 1 0;
  }

  .cookie-banner__manage {
    flex: 1 0 100%;
  }
}

@media (prefers-reduced-motion: reduce) {
  .cookie-fade-enter-active,
  .cookie-fade-leave-active {
    transition: none;
  }
}
</style>
